import { useState } from 'react';

import { useLocation } from 'react-router-dom';

import { notify } from '@cast/design-system';

import { REBALANCE_NODES_KEY } from '@cast/constants';
import { useDeleteNodeDialog } from 'hooks/dialogs';
import { useNodesQuery } from 'hooks/queries/nodes';
import { useNavigate } from '@cast/infrastructure-organization';

import { useCluster } from '@cast/shared-cluster-context-data-access';
import { isNonEmptyArray } from 'utils/types/non-empty-array';

import { useIsKentRebalancingMode } from 'features/cluster/rebalance/_hooks/useIsKentRebalancingMode';
import { isNodeEligibleForRebalancingWizard } from 'features/cluster/rebalance/RebalanceWizard/_utils';

export const useNodeBulkActions = () => {
  const { cluster } = useCluster();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { nodes } = useNodesQuery({ clusterId: cluster.id });
  const { openDeleteNodeDialog } = useDeleteNodeDialog();
  const isKentRebalancingMode = useIsKentRebalancingMode();

  const rebalanceNodes = () => {
    const rebalanceApplicableNodeIds = nodes
      ?.filter(
        (node) =>
          isNodeEligibleForRebalancingWizard({
            node,
            isKentRebalancingMode,
          }) &&
          node.id &&
          selectedIds.includes(node.id)
      )
      .map((node) => node.id);

    if (rebalanceApplicableNodeIds?.length) {
      sessionStorage.setItem(
        REBALANCE_NODES_KEY,
        JSON.stringify(rebalanceApplicableNodeIds)
      );
      navigate(location.pathname.replace('nodes', 'rebalance/new'), {
        descriptor: {
          preserveSearch: true,
        },
      });
    } else {
      notify.error('None of the selected nodes are applicable for rebalancing');
    }
  };

  const deleteNodes = () => {
    const selectedNodes =
      nodes?.filter((node) => selectedIds.includes(node.id)) ?? [];

    if (!isNonEmptyArray(selectedNodes)) {
      return;
    }

    openDeleteNodeDialog({
      cluster,
      nodes: selectedNodes,
      onDeleted: (deletedNodeIds) => {
        setSelectedIds((current) => {
          return current.filter((id) => !deletedNodeIds.includes(id));
        });
      },
    });
  };

  return { selectedIds, setSelectedIds, rebalanceNodes, deleteNodes };
};
